import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion, REGIONS } from "@/contexts/RegionContext";
import { Flag } from "@/components/Flag";
import { VersionBadge } from "@/components/VersionBadge";
import api from "@/lib/api";
import { 
  Building2, 
  FileText, 
  CheckCircle, 
  ClipboardList,
  ChevronRight,
  ChevronDown,
  Bug,
  Users,
  Menu,
  X,
  LogOut,
  User,
  BarChart3,
  Settings,
  Plus,
  Briefcase,
  PiggyBank,
  Calendar,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Scale,
  Gavel,
  Megaphone,
  Link2,
  LineChart,
  FolderOpen,
  Home as HomeIcon,
  Bell,
  Construction,
  Globe,
  Target,
  Shield,
  AlarmClock,
  HandCoins,
  MailOpen,
  Inbox,
  ListChecks,
  Landmark,
  BookOpenCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ReportBugDialog } from "@/components/ReportBugDialog";
import { DockLaunchers } from "@/components/DockLaunchers";
import PhoneShell from "@/components/phone/PhoneShell";
import { useIsPhone } from "@/hooks/useIsPhone";
import { startUpdateWatcher, applyUpdate } from "@/lib/pwa";
import { toast } from "sonner";

// UK Navigation structure with categories
export const ukNavigationCategories = [
  {
    id: "applications",
    label: "Applications",
    icon: FolderOpen,
    items: [
      { path: "/applications", icon: FileText, label: "Underwriting Hub", exact: true, anyPermission: ["can_view_team_cases", "page_underwriting_hub"] },
      { path: "/applications/inbox", icon: Bell, label: "Application Hub", anyPermission: ["can_view_team_cases", "page_applications_inbox"] },
      { path: "/applications/open-banking", icon: Link2, label: "Open Banking", anyPermission: ["can_recommend_credit", "page_open_banking"] },
      { path: "/policy-centre", icon: BookOpenCheck, label: "Policy Centre", anyPermission: ["page_policy_centre", "can_edit_policy", "can_approve_policy"] },
    ]
  },
  {
    id: "sales",
    label: "Sales",
    icon: Briefcase,
    items: [
      { path: "/credit-deals", icon: CheckCircle, label: "Credit Approved Deals", anyPermission: ["can_recommend_credit", "page_sales"] },
      { path: "/sales-calendar", icon: Calendar, label: "Sales Calendar", anyPermission: ["can_recommend_credit", "can_view_reports", "page_sales"] },
      { path: "/sales-analytics", icon: BarChart3, label: "Sales Planning & Analytics", anyPermission: ["can_view_reports", "page_sales"] },
      { path: "/approvals", icon: ClipboardList, label: "Approvals", anyPermission: ["can_approve_credit", "page_sales"] },
    ]
  },
  {
    id: "collections",
    label: "Collections",
    icon: PiggyBank,
    items: [
      { path: "/collections/daily-report", icon: Calendar, label: "Daily Collections Report", permission: "can_view_collections" },
      { path: "/collections/inbox", icon: Inbox, label: "Accounts Inbox", permission: "can_view_collections" },
      { path: "/collections/loan-repayments", icon: HandCoins, label: "Loan Repayments", permission: "can_view_collections" },
      { path: "/collections/ageing", icon: AlarmClock, label: "Loan Arrears Ageing", permission: "can_view_collections" },
      { path: "/collections/portfolio-accounts", icon: Briefcase, label: "Portfolio Accounts", permission: "can_view_collections" },
      { path: "/collections/calendar", icon: Calendar, label: "P2P Calendar", permission: "can_view_collections" },
      { path: "/collections/complaints", icon: Megaphone, label: "Complaints", permission: "can_view_collections" },
      { path: "/collections/analysis", icon: LineChart, label: "Collections Analysis", anyPermission: ["can_view_collections", "can_view_reports"] },
    ]
  },
  {
    id: "legal",
    label: "Legal",
    icon: Scale,
    items: [
      { path: "/legal/documents", icon: FileText, label: "Onboarding Centre", anyPermission: ["can_recommend_credit", "page_onboarding_centre"] },
      { path: "/legal/case-tracker", icon: Gavel, label: "Legal Case Tracker", anyPermission: ["can_view_collections", "page_legal"] },
      { path: "/legal/calendar", icon: Calendar, label: "Legal Calendar", anyPermission: ["can_view_collections", "page_legal"] },
      { path: "/legal/inbox", icon: Inbox, label: "Legal Inbox", anyPermission: ["page_legal", "legal_head"] },
      { path: "/legal/mail-inbox", icon: MailOpen, label: "Postbox", anyPermission: ["can_view_collections", "page_legal"] },
      { path: "/legal/complaints", icon: Megaphone, label: "Complaints", anyPermission: ["can_view_collections", "page_legal"] },
      { path: "/collections/bad-debt", icon: TrendingDown, label: "Bad Debt Tracker", anyPermission: ["can_view_collections", "can_view_reports", "page_legal"] },
      { path: "/legal/performance", icon: LineChart, label: "Performance Analysis", anyPermission: ["can_view_reports", "page_legal"] },
      { path: "/legal/timelines", icon: Scale, label: "Legal settings", anyPermission: ["legal_head", "can_configure_system"] },
      { path: "/portfolio", icon: Shield, label: "Portfolio Analysis", anyRole: ["Admin", "Credit Manager", "Credit TL", "Credit Underwriter", "Collections Manager"] },
    ]
  },
];

// AUS Navigation structure - most items under construction
export const ausNavigationCategories = [
  {
    id: "applications",
    label: "Applications",
    icon: FolderOpen,
    items: [
      { path: "/applications/inbox", icon: Bell, label: "Application Hub", anyPermission: ["can_view_team_cases", "page_applications_inbox"] },
      { path: "/applications", icon: FileText, label: "Underwriting Hub", exact: true, underConstruction: true, permission: "can_view_team_cases" },
      { path: "/add-application", icon: Plus, label: "New Application", underConstruction: true, permission: "can_view_team_cases" },
    ]
  },
  {
    id: "sales",
    label: "Sales",
    icon: Briefcase,
    items: [
      { path: "/credit-deals", icon: CheckCircle, label: "Credit Approved Deals", anyPermission: ["can_recommend_credit", "page_sales"] },
      { path: "/approvals", icon: ClipboardList, label: "Approvals", anyPermission: ["can_approve_credit", "page_sales"] },
    ]
  },
  {
    id: "collections",
    label: "Collections",
    icon: PiggyBank,
    items: [
      { path: "/aus/collections/loan-repayments", icon: HandCoins, label: "Loan Repayments", permission: "can_view_collections" },
      { path: "/aus/collections/inbox", icon: Inbox, label: "Accounts Inbox", permission: "can_view_collections" },
      { path: "/collections/daily-report", icon: Calendar, label: "Collection Report", permission: "can_view_collections" },
      { path: "/aus/collections/ageing", icon: AlarmClock, label: "Loan Arrears Ageing", permission: "can_view_collections" },
      { path: "/aus/collections/portfolio-accounts", icon: Briefcase, label: "Portfolio Accounts", permission: "can_view_collections" },
      { path: "/aus/collections/capay-accounts", icon: Landmark, label: "Capay Settlement Accounts", permission: "can_view_collections" },
      { path: "/aus/collections/calendar", icon: Calendar, label: "P2P Calendar", permission: "can_view_collections" },
      { path: "/collections/analysis", icon: LineChart, label: "Collections Analysis", anyPermission: ["can_view_collections", "can_view_reports"] },
      { path: "/collections/bad-debt", icon: TrendingDown, label: "Bad Debt Tracker", underConstruction: true, anyPermission: ["can_view_collections", "can_view_reports"] },
    ]
  },
  {
    id: "legal",
    label: "Legal",
    icon: Scale,
    items: [
      { path: "/aus/legal/documents", icon: FileText, label: "Onboarding Centre", permission: "can_recommend_credit" },
      { path: "/aus/legal/notices", icon: AlertTriangle, label: "Legal Notices", permission: "can_view_collections" },
      { path: "/aus/legal/inbox", icon: Inbox, label: "Legal Inbox", anyPermission: ["page_legal", "legal_head"] },
      { path: "/legal/case-tracker", icon: Gavel, label: "Legal Case Tracker", underConstruction: true, permission: "can_view_collections" },
      { path: "/legal/performance", icon: LineChart, label: "Legal Performance Analysis", underConstruction: true, permission: "can_view_reports" },
    ]
  },
];

const bottomNavItems = [
  { path: "/audit-trail", icon: ClipboardList, label: "Audit Trail", permission: "can_view_reports" },
];

export const adminNavItems = [
  { path: "/admin/users", icon: Users, label: "User Management", permission: "can_manage_users" },
  { path: "/settings", icon: Settings, label: "Portal Settings", permission: "can_configure_system" },
  // LoanDisk Product Audit page intentionally hidden from the menu after the Oct-2026 backfill; still at /admin/loandisk-products
];

// SimplyFunded brand blue
const SF_BLUE = "#3B70E0";

export default function Layout() {
  const isPhone = useIsPhone();
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 1024);
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  // Wizards fire this event so users can report a bug without closing them
  useEffect(() => {
    const openBug = () => setBugDialogOpen(true);
    window.addEventListener("sf-open-bug-report", openBug);
    return () => window.removeEventListener("sf-open-bug-report", openBug);
  }, []);
  // A new build on the server → one persistent toast; the installed app caches its shell so it has to be told.
  useEffect(() => startUpdateWatcher(() => toast.info("A new version of the portal is ready", {
    id: "sf-update", duration: Infinity, description: "Tap Refresh to pick it up.", action: { label: "Refresh", onClick: applyUpdate },
  })), []);
  // Categories start collapsed on every load — keeps the homepage clean and
  // lets the user expand only the section they want. Expansion state is NOT
  // persisted intentionally (avoids the sidebar feeling "remembered" in a
  // stale way after data changes).
  const [expandedCategories, setExpandedCategories] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, hasPermission, hasAnyPermission, hasAnyRole } = useAuth();
  const { currentRegion, regionConfig, switchRegion, regions, allowedRegions } = useRegion();

  // A tapped push notification lands here when the app is already open (service worker → NAVIGATE)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMsg = (e) => { if (e.data?.type === "NAVIGATE" && e.data.url) { try { const u = new URL(e.data.url); navigate(u.pathname + u.search); } catch { /* ignore */ } } };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);

  // Permission-aware visibility check for any nav item.
  // Falls through to true when item has no explicit gate.
  const canSeeItem = (item) => {
    if (!item) return false;
    if (item.adminOnly && !isAdmin()) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.anyPermission && !hasAnyPermission(item.anyPermission)) return false;
    if (item.anyRole && !hasAnyRole(item.anyRole)) return false;
    return true;
  };

  // Filter raw nav data through the user's permissions. If a category ends
  // up with no visible items, drop the whole category too.
  const rawNavigationCategories = currentRegion === "AUS" ? ausNavigationCategories : ukNavigationCategories;
  const navigationCategories = rawNavigationCategories
    .map(cat => ({ ...cat, items: cat.items.filter(canSeeItem) }))
    .filter(cat => cat.items.length > 0);
  
  // Notification counts for sidebar badges
  const [notifications, setNotifications] = useState({
    pendingApprovals: 0,
    pendingDocRequests: 0,
    legalMailInbox: 0,
    accountsInbox: 0,
    legalInbox: 0,
    appsInbox: 0,
    myActions: 0,
    myActionsOverdue: 0,
  });

  // Fetch notification counts
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        let pendingApprovals = 0;
        let pendingDocRequests = 0;
        
        // Fetch all pending approvals using the /approvals endpoint
        try {
          const userRole = (user?.role || 'admin').toLowerCase();
          const approvalsResponse = await api.get(`/approvals?user_role=${userRole}&region=${currentRegion}`);
          const data = approvalsResponse.data;
          
          // Count all pending items
          pendingApprovals = (
            (data?.bank_details?.length || 0) +
            (data?.loandisk_requests?.length || 0) +
            (data?.disbursement_approvals?.length || 0) +
            (data?.net_in_hand_approvals?.length || 0) +
            (data?.consolidation_approvals?.length || 0)
          );
        } catch (err) {
          // Silently fail - notifications will show 0
        }
        
        // Fetch pending document requests count
        try {
          const docRequestsResponse = await api.get('/contracts/requests/pending');
          pendingDocRequests = docRequestsResponse.data?.requests?.length || 0;
        } catch (err) {
          // Silently fail - notifications will show 0
        }
        
        // Pending mail counts for the two inboxes (parallel, fail-safe)
        let legalMailInbox = 0;
        let accountsInbox = 0;
        let legalInbox = 0;
        let appsInbox = 0;
        let myActions = 0;
        let myActionsOverdue = 0;
        const deptCount = (dept) => api.get(`/inbox/${dept}/count`, { params: { region: currentRegion } });
        const [legalRes, appsRes, myActionsRes, accountsRes, legalInboxRes, sumsubRes] = await Promise.allSettled([
          api.get('/legal/mail-inbox/count'),
          api.get('/applications/inbox/count', { params: { region: currentRegion } }),
          user?.email
            ? api.get(`/my-actions/badge?email=${encodeURIComponent(user.email)}`)
            : Promise.reject(new Error('no email')),
          deptCount('accounts'),
          deptCount('legal'),
          api.get('/sumsub-hub/attention', { params: { region: currentRegion } }),
        ]);
        if (legalRes.status === 'fulfilled') legalMailInbox = legalRes.value.data?.count || 0;
        if (appsRes.status === 'fulfilled') appsInbox = appsRes.value.data?.count || 0;
        // new Sumsub Digital ID results (approved / failed / on hold, not yet looked at) roll into the Application Hub pill
        if (sumsubRes.status === 'fulfilled') appsInbox += sumsubRes.value.data?.count || 0;
        if (accountsRes.status === 'fulfilled') accountsInbox = accountsRes.value.data?.count || 0;
        if (legalInboxRes.status === 'fulfilled') legalInbox = legalInboxRes.value.data?.count || 0;
        if (myActionsRes.status === 'fulfilled') {
          const c = myActionsRes.value.data?.counts || {};
          myActions = c.total || 0;
          myActionsOverdue = (c.overdue || 0) + (c.due_today || 0);
        }
        
        setNotifications({
          pendingApprovals,
          pendingDocRequests,
          legalMailInbox,
          accountsInbox,
          legalInbox,
          appsInbox,
          myActions,
          myActionsOverdue,
        });
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    // Fetch initially
    if (user) {
      fetchNotifications();
    }
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      if (user) {
        fetchNotifications();
      }
    }, 30000);
    
    // Refresh immediately when the tab regains focus (background tabs are throttled)
    const onVisible = () => {
      if (user && document.visibilityState === "visible") fetchNotifications();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentRegion]);

  // Build bottom nav items, filtered by permission so non-admins don't see
  // dead links to admin pages.
  const bottomNav = [...bottomNavItems, ...adminNavItems].filter(canSeeItem);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isItemActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const isCategoryActive = (category) => {
    return category.items.some(item => isItemActive(item.path, item.exact));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (isPhone) {
    return (
      <>
        <PhoneShell notifications={notifications} navigationCategories={navigationCategories} bottomNav={bottomNav} onReportBug={() => setBugDialogOpen(true)} />
        <ReportBugDialog open={bugDialogOpen} onOpenChange={setBugDialogOpen} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border transition-all duration-300",
          sidebarOpen ? "w-64" : "w-16"
        )}
      >
        {/* Logo */}
        <div className="h-20 flex items-center px-4 border-b border-border">
          {sidebarOpen ? (
            <img 
              src="https://customer-assets.emergentagent.com/job_3b0f0214-cf63-4bc5-97a1-4b3e15ccb75f/artifacts/13s1d0fc_e29fbeb6-4e6b-4cb4-b2f9-06ccc88daf69.jpg" 
              alt="SimplyFunded by Morley Capital" 
              className="h-14 w-auto object-contain"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: SF_BLUE }}>
              <span className="text-white font-bold text-sm">SF</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 flex flex-col overflow-y-auto">
          {/* Home — always visible, sits above the categories so the user
              can jump back to the launchpad with one click. */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mb-2",
                isActive
                  ? "bg-primary text-white font-medium"
                  : "text-foreground hover:bg-slate-100",
                !sidebarOpen && "justify-center"
              )
            }
            data-testid="nav-home"
            title="Home"
          >
            <HomeIcon className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Home</span>}
          </NavLink>

          {/* Category-based Navigation */}
          <div className="space-y-1">
            {navigationCategories.map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const isActive = isCategoryActive(category);
              
              return (
                <div key={category.id} className="space-y-0.5">
                  {/* Category Header */}
                  {(() => {
                    // Calculate total notifications for this category
                    let categoryNotifications = 0;
                    if (category.id === 'sales') {
                      categoryNotifications = notifications.pendingApprovals;
                    } else if (category.id === 'legal') {
                      categoryNotifications = notifications.pendingDocRequests + notifications.legalMailInbox + notifications.legalInbox;
                    } else if (category.id === 'collections') {
                      categoryNotifications = notifications.accountsInbox;
                    } else if (category.id === 'applications') {
                      categoryNotifications = notifications.appsInbox;
                    }
                    
                    return (
                      <button
                        onClick={() => sidebarOpen && toggleCategory(category.id)}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative",
                          isActive 
                            ? "bg-primary/10 text-primary" 
                            : "text-muted-foreground hover:bg-slate-100 hover:text-foreground",
                          !sidebarOpen && "justify-center"
                        )}
                        data-testid={`nav-category-${category.id}`}
                      >
                        <div className="relative">
                          <category.icon className="w-5 h-5 flex-shrink-0" />
                          {/* Red dot on collapsed category icon */}
                          {!sidebarOpen && categoryNotifications > 0 && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                          )}
                        </div>
                        {sidebarOpen && (
                          <>
                            <span className="flex-1 text-left">{category.label}</span>
                            {/* Notification count on category when collapsed items */}
                            {!isExpanded && categoryNotifications > 0 && (
                              <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-red-500 text-white animate-pulse">
                                {categoryNotifications > 99 ? '99+' : categoryNotifications}
                              </span>
                            )}
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </>
                        )}
                      </button>
                    );
                  })()}
                  
                  {/* Category Items (collapsible) */}
                  {sidebarOpen && isExpanded && (
                    <div className="ml-4 pl-3 border-l border-slate-200 space-y-0.5">
                      {category.items.map((item) => {
                        // Determine if this item has a notification
                        let notificationCount = 0;
                        if (item.path === '/approvals') {
                          notificationCount = notifications.pendingApprovals;
                        } else if (item.path === '/legal/documents' || item.path === '/aus/legal/documents') {
                          notificationCount = notifications.pendingDocRequests;
                        } else if (item.path === '/legal/mail-inbox') {
                          notificationCount = notifications.legalMailInbox;
                        } else if (item.path === '/legal/inbox' || item.path === '/aus/legal/inbox') {
                          notificationCount = notifications.legalInbox;
                        } else if (item.path === '/collections/inbox' || item.path === '/aus/collections/inbox') {
                          notificationCount = notifications.accountsInbox;
                        } else if (item.path === '/applications/inbox') {
                          notificationCount = notifications.appsInbox;
                        }
                        
                        // Check if item is under construction (AUS region)
                        const isUnderConstruction = item.underConstruction;
                        
                        if (isUnderConstruction) {
                          return (
                            <div
                              key={item.path}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground/50 cursor-not-allowed"
                              title="Coming Soon - Under Construction"
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-disabled`}
                            >
                              <item.icon className="w-4 h-4 flex-shrink-0 opacity-50" />
                              <span className="flex-1 opacity-50">{item.label}</span>
                              <Construction className="w-3 h-3 text-amber-500" />
                            </div>
                          );
                        }
                        
                        // Handle items with subItems (nested navigation)
                        if (item.subItems) {
                          const isSubActive = item.subItems.some(sub => isItemActive(sub.path));
                          return (
                            <div key={item.path} className="space-y-0.5">
                              <div
                                className={cn(
                                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                                  isSubActive
                                    ? "bg-primary/10 text-primary font-medium"
                                    : "text-muted-foreground"
                                )}
                              >
                                <item.icon className="w-4 h-4 flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                              </div>
                              <div className="ml-4 pl-2 border-l border-slate-200 space-y-0.5">
                                {item.subItems.map((subItem) => (
                                  <NavLink
                                    key={subItem.path}
                                    to={subItem.path}
                                    className={cn(
                                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors",
                                      isItemActive(subItem.path)
                                        ? "bg-primary text-white font-medium"
                                        : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                                    )}
                                    data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <subItem.icon className="w-3 h-3 flex-shrink-0" />
                                    <span>{subItem.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            className={cn(
                              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors relative",
                              isItemActive(item.path, item.exact)
                                ? "bg-primary text-white font-medium"
                                : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                            )}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1">{item.label}</span>
                            {/* Notification badge */}
                            {notificationCount > 0 && (
                              <span className={cn(
                                "min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center",
                                isItemActive(item.path, item.exact)
                                  ? "bg-white text-primary"
                                  : "bg-red-500 text-white animate-pulse"
                              )}>
                                {notificationCount > 99 ? '99+' : notificationCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Comms launchers — Slack & Aircall docks */}
          <div className="space-y-1 border-t border-border pt-3 pb-1">
            <DockLaunchers sidebarOpen={sidebarOpen} />
          </div>

          {/* Bottom Nav Items (filtered by permission already) */}
          <div className="space-y-1 border-t border-border pt-3">
            {bottomNav.map((item) => {
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                    isItemActive(item.path, item.exact)
                      ? "bg-primary text-white font-medium"
                      : "text-muted-foreground hover:bg-slate-100 hover:text-foreground",
                    !sidebarOpen && "justify-center"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={item.label}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Toggle Button + Report a bug */}
        <div className="p-3 border-t border-border flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBugDialogOpen(true)}
            className={cn("text-red-500 hover:text-red-600 hover:bg-red-50", sidebarOpen ? "flex-1 justify-start gap-2" : "w-full justify-center")}
            data-testid="report-bug-btn"
            title="Report a bug"
          >
            <Bug className="w-4 h-4" />
            {sidebarOpen && <span className="text-xs">Report a bug</span>}
          </Button>
          {sidebarOpen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="justify-center"
              data-testid="sidebar-toggle"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Button>
          )}
        </div>
        {!sidebarOpen && (
          <div className="px-3 pb-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full justify-center"
              data-testid="sidebar-toggle"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        )}
        <ReportBugDialog open={bugDialogOpen} onOpenChange={setBugDialogOpen} />

        {/* User Info */}
        <div className="p-4 border-t border-border">
          {sidebarOpen ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.role || "Guest"}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <Settings className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="sign-out-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-full" data-testid="user-menu-trigger-collapsed">
                  <User className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <Settings className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="sign-out-menu-item-collapsed">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "flex-1 min-h-screen min-w-0 transition-all duration-300",
        sidebarOpen ? "ml-64" : "ml-16"
      )}>
        {/* Top header bar */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-3 sm:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            {/* AUS Under Construction Banner */}
            {/* AUS construction banner removed — AU portal is live */}
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* My Actions — personal to-do pill. Compact button that
                pulses when there's overdue / due-today work. Deep-links
                to the full My Actions page. */}
            <button
              onClick={() => navigate('/my-actions')}
              className={cn(
                "relative flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                notifications.myActionsOverdue > 0
                  ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  : notifications.myActions > 0
                  ? "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              )}
              data-testid="topbar-my-actions"
              title="My Actions — everything assigned to me"
            >
              <ListChecks className="w-4 h-4" />
              <span className="hidden sm:inline">My Actions</span>
              {notifications.myActions > 0 && (
                <span className={cn(
                  "min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center",
                  notifications.myActionsOverdue > 0 ? "bg-red-500 text-white animate-pulse" : "bg-slate-300 text-slate-800"
                )}>
                  {notifications.myActions > 99 ? '99+' : notifications.myActions}
                </span>
              )}
            </button>
            <VersionBadge />
            {/* Region Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-2"
                  data-testid="region-switcher"
                >
                  <Flag code={regionConfig?.flagCode} size={20} />
                  <span className="hidden sm:inline text-sm">{regionConfig?.name}</span>
                  <Globe className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Switch Region
                </div>
                <DropdownMenuSeparator />
                {Object.entries(regions).map(([code, region]) => {
                  const notGranted = !region.comingSoon && !allowedRegions.includes(code);
                  const disabled = !!region.comingSoon || notGranted;
                  return (
                    <DropdownMenuItem 
                      key={code}
                      onClick={() => {
                        if (disabled) return;
                        switchRegion(code);
                        // Always land on Home after a region switch
                        navigate("/");
                      }}
                      disabled={disabled}
                      title={notGranted ? "You don't have access to this region. Ask an admin to grant it in User Settings." : undefined}
                      className={cn(
                        "flex items-center gap-2 cursor-pointer",
                        currentRegion === code && "bg-blue-50",
                        disabled && "opacity-60 cursor-not-allowed"
                      )}
                      data-testid={`region-option-${code}`}
                    >
                      <Flag code={region.flagCode} size={22} />
                      <span className="flex-1">{region.name}</span>
                      {region.comingSoon ? (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          Soon
                        </span>
                      ) : notGranted ? (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                          No access
                        </span>
                      ) : currentRegion === code && (
                        <Badge variant="secondary" className="text-xs">Active</Badge>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <span className="hidden md:inline text-sm text-muted-foreground">
              {user?.department || "SimplyFunded"}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-3 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
